import { useMemo, useCallback, useRef } from 'react';
import { IdeasPopup } from '@divlab/divan-constructor';
import { cn } from '@divlab/divanui';

import * as HeaderStore from '@Stores/Header';
import { LandingProductsSection, PinsBlock, FourCategoryTiles } from '@LandingComponents/index';
import useTranslation from '@Queries/useTranslation';
import styles from './Categories.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { CategoryData, CategoryHash } from '@Pages/PageHranenie/typings';

interface CategoriesProps extends HTMLAttributes<HTMLDivElement> {
  classNameCategory?: string;
  categories: CategoryData[];
  analylicsTitle: string;
}

const Categories: FC<CategoriesProps> = (props) => {
  const { className, categories, classNameCategory, analylicsTitle, ...restProps } = props;
  const { t } = useTranslation();
  const { totalHeight } = HeaderStore.useSizes();
  const ref = useRef<HTMLDivElement>(null);

  const mappedCategories = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        pins: category.pins.map((product, index) => ({
          ...product,
          text: t('ui.more'),
          coords: category.pinsCoords[index],
        })),
      })),
    [categories, t],
  );

  const scrollToElem = useCallback(
    (elem: HTMLDivElement) => {
      const rect = elem.getBoundingClientRect();
      const top = window.scrollY + rect.top - totalHeight;

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight],
  );

  const handleMoreDetails = useCallback(
    (hash: CategoryHash) => {
      const elem = ref.current.querySelector<HTMLDivElement>(`#${hash}`);

      if (elem) {
        window.history.replaceState({}, '', `#${hash}`);
        scrollToElem(elem);
      }
    },
    [scrollToElem],
  );

  return (
    <div className={className} {...restProps} ref={ref}>
      <FourCategoryTiles
        className={styles.tiles}
        enlargedText
        title={t('furniture.title')}
        description={t('furniture.description')}
        categories={categories}
        analylicsTitle={analylicsTitle}
        onMoreDetails={handleMoreDetails}
      />
      {mappedCategories.map((category, index) => {
        return (
          <div className={styles.container} key={category.title} id={category.hash}>
            <PinsBlock
              analylicsTitle={analylicsTitle}
              className={cn(styles.category, classNameCategory, {
                [styles.right]: index % 2 === 0,
                [styles.left]: index % 2 !== 0,
              })}
              side={index % 2 !== 0 ? 'left' : 'right'}
              renderPopup={({ product, pin, width, withoutCurrency, popupPosition, refPopup }) => (
                <IdeasPopup
                  className={styles.popup}
                  product={product}
                  ref={refPopup}
                  style={{ ...popupPosition, width: `${width}px` }}
                  listTitle={analylicsTitle}
                  withoutCurrency={withoutCurrency}
                  pinText={pin.text as string}
                />
              )}
              title={category.title}
              image={category.image}
              description={category.description}
              pins={category.pins}
            />
            <LandingProductsSection
              products={category.products}
              button={{ link: category.link }}
              analyticsTitle={analylicsTitle}
            />
          </div>
        );
      })}
    </div>
  );
};

export default Categories;
